import { CloseIcon } from '@chakra-ui/icons'
import { Box, Button, Center, Collapse, Divider, Flex, Text } from '@chakra-ui/react'
import { useApp, useAppModals, usePopUpMessage } from '@hb/react'
import {
  FieldMapValue,
  newPatientConfirmField,
  newPatientReviewField,
  ProfileType,
  profileTypes,
  USER_ROLES,
  UserRoleItem,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'
import { collection, CollectionReference, getDocs, query, Query, where } from 'firebase/firestore'
import { AnimatePresence } from 'framer-motion'
import { useCallback as providerPracticeId, useEffect, useMemo, useState } from 'react'
import { inviteUser } from '../../backend/auth/utils'
import { db } from '../../backend/db/db'
import { addPatientToPracticeFunction } from '../../backend/functions/practices'
import { optionalPracticeSelectField, practiceSelectField } from '../../collections/fields/practice'
import { useSelectedPractice } from '../../hooks/backend/auth/useSelectedPractice'
import { useNavigate } from '../../hooks/navigation'
import { GrowFadeBox } from '../Animation/GrowFadeBox'
import { ActionButton, SolidActionButton } from '../Buttons'
import { SimpleForm } from '../forms/FinalForm/SimpleForm'
import { StandaloneInput } from '../forms/Input'
import { Loading } from '../Loading'
import { UserPreview } from '../Users/UserPreview'
import { DefaultModal } from './DefaultModal'

const fetchMatches = async (formData: FieldMapValue) => {
  const { fname, lname, email, dob } = formData

  if (!fname) throw new Error('First name is required')
  if (!lname) throw new Error('Last name is required')
  const userRolesCollection = collection(db, USER_ROLES) as CollectionReference<UserRoleItem>
  const processedFname = (fname ?? '').trim().toLowerCase()
  const processedLname = (lname ?? '').trim().toLowerCase()
  const nameQuery = query(
    userRolesCollection,
    where('names', 'array-contains', `${processedFname} ${processedLname}`),
  )

  const queries: Query<UserRoleItem>[] = [nameQuery]
  if (dob) {
    queries.push(
      query(
        userRolesCollection,
        where('lnameLower', '==', processedLname),
        where('dob', '==', formData.dob),
      ),
    )
  }
  if (email) {
    queries.push(query(userRolesCollection, where('emailLower', '==', email.toLowerCase().trim())))
  }

  const matches: Record<string, UserRoleItem> = {}
  const promises = queries.map(q =>
    getDocs(q).then(snap => {
      snap.forEach(doc => {
        matches[doc.id] = { ...doc.data(), id: doc.id }
      })
    }),
  )
  await Promise.all(promises)
  return Object.values(matches)
}

const ProfileTypeSelect = ({
  profileType,
  onChange,
}: {
  profileType: ProfileType | null
  onChange: (updated: ProfileType) => void
}) => (
  <Flex align="center" pt={1} gap={2} flexFlow="column" w="100%">
    <Text opacity={0.8} lineHeight={1} fontFamily="Hero-New">
      What is the nature of the initial care?
    </Text>
    <Flex flexFlow="row" gap={2} px={2} w="100%">
      {profileTypes.map(value => (
        <Button
          flex={1}
          key={value}
          variant="unstyled"
          display="flex"
          alignItems="center"
          color={profileType === value ? 'white' : 'gray.700'}
          textShadow={profileType === value ? '1px 1px 2px #00000066' : 'none'}
          border="1px solid"
          borderColor={profileType === value ? 'green.500' : 'gray.300'}
          bg={profileType === value ? 'green.500' : 'white'}
          size="sm"
          isActive={profileType === value}
          onClick={() => {
            onChange(value)
          }}>
          <Text position="relative" bottom="1px">
            {value === 'gyn' ? 'GYN Care' : 'Pregnancy'}
          </Text>
        </Button>
      ))}
    </Flex>
    <AnimatePresence mode="popLayout">
      {profileType === 'gyn' ? (
        <GrowFadeBox
          overflow="hidden"
          borderRadius={3}
          boxShadow="md"
          maxW="270px"
          px={3}
          py={1.5}
          bg="white"
          key="gyn">
          <Text
            fontWeight={500}
            fontFamily="Hero-New"
            textAlign="center"
            fontSize="sm"
            opacity={0.8}>
            Patient profile will display GYN care by default.
          </Text>
        </GrowFadeBox>
      ) : null}
      {
        // This is a new feature that was added in the new version of the app
        profileType === 'pregnancy' ? (
          <GrowFadeBox
            overflow="hidden"
            borderRadius={3}
            px={3}
            maxW="270px"
            boxShadow="md"
            py={1.5}
            bg="white"
            key="pregnancy">
            <Text
              fontWeight={500}
              fontFamily="Hero-New"
              textAlign="center"
              color="gray.700"
              fontSize="sm"
              opacity={0.8}>
              Patient profile will display pregnancy info by default.
            </Text>
          </GrowFadeBox>
        ) : null
      }
    </AnimatePresence>
  </Flex>
)

const InvitePatientReview = ({
  formData,
  onBack,
  onComplete,
}: {
  formData: FieldMapValue | null
  onBack: () => void
  onComplete: () => void
}) => {
  const { showMessage } = usePopUpMessage()
  const [loadingMatches, setLoadingMatches] = useState(false)
  const [creatingNew, setCreatingNew] = useState(false)
  const [profileType, setProfileType] = useState<ProfileType | null>(null)
  const [matches, setMatches] = useState<UserRoleItem[] | null>(null)

  const navigate = useNavigate()
  useEffect(() => {
    if (!formData) return
    setLoadingMatches(true)
    fetchMatches(formData)
      .then(matches => {
        setMatches(matches)
      })
      .catch(e => {
        showMessage({
          text: 'Error checking existing patients',
          subText: e.message,
          type: 'error',
        })
      })
      .finally(() => {
        setLoadingMatches(false)
      })
  }, [formData, showMessage])

  const handleBack = providerPracticeId(() => {
    setMatches(null)
    setCreatingNew(false)
    onBack()
  }, [onBack])

  const { appName } = useApp()
  const { id: selectedPracticeId } = useSelectedPractice()
  const [adminPracticeId, setAdminPracticeId] = useState<string | null>(null)
  const practiceId = useMemo(() => {
    if (appName === 'providers-app') return selectedPracticeId
    return adminPracticeId
  }, [appName, selectedPracticeId, adminPracticeId])

  const handleConfirmInvite = providerPracticeId(
    async (data: FieldMapValue) => {
      if (!profileType) return { [FORM_ERROR]: 'Profile type is required' }
      if (profileType === 'gyn' && !practiceId)
        return { [FORM_ERROR]: 'Practice is required for GYN care' }
      const submitted = { ...formData, ...data }
      try {
        const { patientId, pregnancyId } = await inviteUser(
          appName,
          submitted,
          profileType,
          practiceId,
        )
        showMessage({
          type: 'success',
          text: submitted?.email ? 'Patient invited' : 'Profile created',
        })
        const path = pregnancyId
          ? `/pregnancies/${pregnancyId}/pregnancy/questionnaire`
          : `/patients/${patientId}`
        navigate(appName === 'app' ? `/admin${path}` : path)
        onComplete()
        return undefined
      } catch (e: any) {
        showMessage({
          text: 'Error inviting patient',
          subText: e.message,
          type: 'error',
        })
        return { [FORM_ERROR]: e.message || 'Error inviting patient' }
      }
    },
    [formData, appName, selectedPracticeId, showMessage, navigate, profileType, onComplete],
  )

  const handleAddToPractice = providerPracticeId(
    async (patientId: string) => {
      if (!practiceId) return
      try {
        await addPatientToPracticeFunction({ patientId, practiceId })
        showMessage({ type: 'success', text: 'Patient added to practice' })
        navigate(appName === 'app' ? `/admin/patients/${patientId}` : `/patients/${patientId}`)
        onComplete()
      } catch (e: any) {
        showMessage({
          text: 'Error adding patient to practice',
          subText: e.message,
          type: 'error',
        })
      }
    },
    [practiceId, appName, showMessage, navigate, onComplete],
  )

  const initValue = useMemo(
    () => ({
      email: formData?.email ?? '',
      dob: formData?.dob ?? undefined,
    }),
    [formData],
  )

  return (
    <Flex flexFlow="column" w="100%">
      <Collapse style={{ width: '100%' }} in={loadingMatches && !creatingNew}>
        <Center w="100%">
          <Loading text="Loading potential matches" />
        </Center>
      </Collapse>
      <Collapse style={{ width: '100%' }} in={!loadingMatches && !creatingNew}>
        <Flex flexFlow="column" w="100%">
          <Flex borderBottom="1px solid #cdcdcd" py={1.5} px={2} align="center" width="100%">
            <Text opacity={0.8} lineHeight={1} fontWeight={500} fontSize="lg" fontFamily="Hero-New">
              Matches
            </Text>
          </Flex>
          <Flex
            bg="#e4e4e4"
            maxH="350px"
            overflowY="auto"
            gap={2}
            py={1}
            px={2}
            flexFlow="column"
            width="100%">
            {matches?.length ? (
              matches.map(m => {
                const notInPractice = practiceId && !m.midwives?.includes(practiceId)
                return (
                  <UserPreview
                    key={m.id}
                    goTo={!notInPractice}
                    userId={m.id}
                    onAddToPractice={notInPractice ? () => handleAddToPractice(m.id) : undefined}
                  />
                )
              })
            ) : (
              <Text fontStyle="italic">No matching patients found</Text>
            )}
          </Flex>
          <Flex
            borderTop="1px solid #cdcdcd"
            gap={2}
            bg="gray.50"
            justify="flex-end"
            p={2}
            w="100%"
            align="center">
            <ActionButton size="sm" onClick={handleBack}>
              Back
            </ActionButton>
            <SolidActionButton
              size="sm"
              onClick={() => {
                setCreatingNew(true)
              }}>
              {formData?.email ? 'Invite New Patient' : 'Create New Profile'}
            </SolidActionButton>
          </Flex>
        </Flex>
      </Collapse>
      <Collapse style={{ width: '100%' }} in={creatingNew}>
        <Flex px={1} pt={3} flexFlow="column" w="100%">
          <Flex flexFlow="column" px={2} pb={1} width="100%">
            <Text fontFamily="Hero-New" lineHeight={1} fontWeight={500} opacity={0.85}>
              Create New Profile for {formData?.fname ?? ''}
              {formData?.middleName ? ` ${formData.middleName}` : ''} {formData?.lname ?? ''}
            </Text>
          </Flex>
          <Divider my={1} />
          <ProfileTypeSelect profileType={profileType} onChange={setProfileType} />
          <Divider mb={1} mt={2} />
          {appName === 'app' ? (
            <>
              <Flex flexFlow="column" gap={1} py={2} px={3}>
                <Text opacity={0.8} lineHeight={1} fontFamily="Hero-New">
                  Care Provider
                </Text>
                <StandaloneInput
                  theme="detailed"
                  value={adminPracticeId}
                  field={profileType === 'gyn' ? practiceSelectField : optionalPracticeSelectField}
                  onChange={setAdminPracticeId}
                />
              </Flex>
              <Divider />
            </>
          ) : null}
          <SimpleForm
            canSubmitClean
            value={initValue}
            boxProps={{
              p: 0,
              bg: 'transparent',
              boxShadow: 'none',
            }}
            field={newPatientConfirmField}
            onSubmit={handleConfirmInvite}
          />
        </Flex>
      </Collapse>
    </Flex>
  )
}

const InvitePatientContent = ({ onClose }: { onClose: () => void }) => {
  const [formData, setFormData] = useState<FieldMapValue | null>(null)

  return (
    <Flex bg="gray.100" flexFlow="column" w="100%">
      <Flex borderBottom="1px solid #cdcdcd" bg="gray.50" px={3} py={1} align="center" width="100%">
        <Text fontFamily="Hero-New" fontSize="lg" fontWeight={400} opacity={0.8}>
          Invite Patient
        </Text>
        <Button
          size="xs"
          bg="whiteAlpha.800"
          borderRadius="full"
          variant="outline"
          color="gray.700"
          gap={1}
          ml="auto"
          onClick={onClose}>
          <CloseIcon opacity={0.8} w={2} h={2} />
          <Text>CLOSE</Text>
        </Button>
      </Flex>
      <Flex flexFlow="column" w="100%">
        <Collapse in={!formData} style={{ width: '100%' }}>
          <Box w="100%" px={2} py={1}>
            <SimpleForm
              boxProps={{
                p: 0,
                bg: 'transparent',
                boxShadow: 'none',
              }}
              field={newPatientReviewField}
              onSubmit={async data => {
                setFormData(data)
                return undefined
              }}
            />
          </Box>
        </Collapse>
        <Collapse in={!!formData} style={{ width: '100%' }}>
          <InvitePatientReview
            onComplete={onClose}
            onBack={() => {
              setFormData(null)
            }}
            formData={formData}
          />
        </Collapse>
      </Flex>
    </Flex>
  )
}
export const InvitePatientModal = () => {
  const { openId, close } = useAppModals(s => s.invitePatient)
  return (
    <DefaultModal
      isOpen={openId === 'root'}
      onClose={close}
      overlayHeader
      size="lg"
      closeDisabled
      render={() => <InvitePatientContent onClose={close} />}
    />
  )
}
